import React, { useEffect, useState } from 'react';
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../../store';
import { SurveyType } from '../../../../../shared/constants';
import {
  INSIGHTS_SURVEY_TYPES,
  SURVEY_TYPE_DISPLAY_NAME,
} from '../../../../../shared/surveys';
import { fetchFacilitySurveyInsights } from '../../../../../store/facility/actionCreators';
import { getBrowserTimeZone } from '../../../../../shared/utils';
import { dimGrey, lightBlack } from '../../../../../mainTheme';
import type { TabProps } from '../../FacilityDetails';
import {
  DEFAULT_INSIGHTS_WINDOW,
  formatPeriodRange,
  INSIGHTS_WINDOWS,
  InsightsWindow,
} from '../../../../Patients/PatientDetails/components/InsightsTab/surveyInsights';
import FacilitySurveyInsightsView from './FacilitySurveyInsightsView';

const toggleSx = { px: 2, textTransform: 'none', fontWeight: 700 };

// Location Insights: one dashboard view per survey type over a rolling window,
// from GET /facility/:id/survey_insights (every patient of the location, all
// survey types in one call). The window ends today in the browser's zone so
// its day buckets match the local dates the viewer sees.
const BROWSER_TIME_ZONE = getBrowserTimeZone();

const InsightsTab: React.FC<TabProps> = ({ locationId }) => {
  const dispatch = useDispatch();
  const [surveyType, setSurveyType] = useState<SurveyType>(
    INSIGHTS_SURVEY_TYPES[0],
  );
  const [insightsWindow, setInsightsWindow] = useState<InsightsWindow>(
    DEFAULT_INSIGHTS_WINDOW,
  );

  // Field-level selectors so the tab only re-renders for its own data.
  const facilitySurveyInsights = useSelector(
    (state: RootState) => state.facility.facilitySurveyInsights,
  );
  const facilitySurveyInsightsError = useSelector(
    (state: RootState) => state.facility.facilitySurveyInsightsError,
  );

  const facilityId = locationId ?? null;
  const apiDays = insightsWindow.days;
  const insightsForSelection =
    facilityId !== null &&
    facilitySurveyInsights?.facilityId === facilityId &&
    facilitySurveyInsights.requestedDays === apiDays
      ? facilitySurveyInsights
      : null;

  // Fetch whenever the cache does not match the current location/window. The
  // slice's loading flag is not a gate: overlapping requests are safe because
  // the thunk carries a sequence id and the reducer drops superseded
  // responses. A failed fetch leaves the cache unchanged, so it is not
  // retried in a loop.
  useEffect(() => {
    if (facilityId !== null && !insightsForSelection) {
      dispatch(
        fetchFacilitySurveyInsights(
          facilityId,
          apiDays,
          insightsWindow.granularity,
          BROWSER_TIME_ZONE,
        ),
      );
    }
  }, [
    dispatch,
    facilityId,
    apiDays,
    insightsWindow.granularity,
    insightsForSelection,
  ]);

  const period = insightsForSelection?.period;
  const periodRange = formatPeriodRange(period);
  // An error only counts when nothing is cached for this selection: a stale
  // error from an earlier selection must not mask data that did load.
  const error = insightsForSelection ? null : facilitySurveyInsightsError;
  const pending = !insightsForSelection && !error;
  const block =
    insightsForSelection?.surveys.find(
      (b) => Number(b?.survey_type_id) === surveyType,
    ) ?? null;

  // Toggle label with the window's response count; no count while the
  // insights are missing, so a failed fetch never reads as "(0)".
  const surveyTypeLabel = (type: SurveyType) => {
    const name = SURVEY_TYPE_DISPLAY_NAME[type];
    if (!insightsForSelection) return name;
    const count =
      insightsForSelection.surveys.find(
        (b) => Number(b?.survey_type_id) === type,
      )?.response_count ?? 0;
    return `${name} (${count})`;
  };

  return (
    <Box display="flex" flexDirection="column" gap={3} px={2} pt={1}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={2}
      >
        <Box>
          <Typography
            sx={{
              color: lightBlack,
              fontSize: '16px',
              fontWeight: 700,
              lineHeight: '24px',
              letterSpacing: '0.2px',
            }}
          >
            Survey insights
          </Typography>
          <Typography
            sx={{
              color: dimGrey,
              fontSize: '12px',
              fontWeight: 500,
              lineHeight: '16px',
            }}
          >
            How this location&apos;s patients are responding to their surveys
            over time
          </Typography>
        </Box>
        <Box display="flex" flexWrap="wrap" gap={2}>
          <ToggleButtonGroup
            value={surveyType}
            exclusive
            size="small"
            color="secondary"
            aria-label="Survey type"
            onChange={(_, value: SurveyType | null) => {
              if (value !== null) setSurveyType(value);
            }}
          >
            {INSIGHTS_SURVEY_TYPES.map((type) => (
              <ToggleButton key={type} value={type} sx={toggleSx}>
                {surveyTypeLabel(type)}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <ToggleButtonGroup
            value={insightsWindow.days}
            exclusive
            size="small"
            color="secondary"
            aria-label="Time window"
            onChange={(_, value: number | null) => {
              const next = INSIGHTS_WINDOWS.find((w) => w.days === value);
              if (next) setInsightsWindow(next);
            }}
          >
            {INSIGHTS_WINDOWS.map((w) => (
              <ToggleButton key={w.days} value={w.days} sx={toggleSx}>
                {w.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          {periodRange && (
            <Typography
              data-testid="facility-insights-period-range"
              sx={{
                alignSelf: 'center',
                color: dimGrey,
                fontSize: '12px',
                fontWeight: 500,
                lineHeight: '16px',
                whiteSpace: 'nowrap',
              }}
            >
              {periodRange}
            </Typography>
          )}
        </Box>
      </Box>
      <FacilitySurveyInsightsView
        key={surveyType}
        surveyType={surveyType}
        block={block}
        period={period}
        patientCount={insightsForSelection?.patient_count ?? 0}
        windowLabel={insightsWindow.label}
        granularity={insightsWindow.granularity}
        loading={pending}
        error={error}
      />
    </Box>
  );
};

export default InsightsTab;
